import React, {CSSProperties, HTMLAttributes, KeyboardEvent} from 'react'

import {focusableClass, ensureFocusStyles} from '../styles.js'
import {iconColors, iconSizes} from './styles.js'

ensureFocusStyles()

interface IconProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
    /** Icon class, e.g. 'mdi mdi-check' */
    icon: string
    /** xs 12, sm 14, md 16, lg 20, 18 by default */
    size?: 'xs' | 'sm' | 'md' | 'lg'
    color?: string
    style?: CSSProperties
}

/**
 * An icon font glyph. It has no margins. Without onClick or aria-label it is decorative (aria-hidden); with an
 * aria-label it is an image; with onClick it is a button the keyboard reaches and Enter or Space clicks (give it an
 * aria-label then).
 */
export default function Icon({icon, size, color, className, style, onClick, onKeyDown, ...rest}: IconProps) {
    const clickable = !!onClick
    const named = !!rest['aria-label']

    const keyDown = (event: KeyboardEvent<HTMLElement>) => {
        onKeyDown?.(event)

        if (clickable && !event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) {
            // Space would scroll the page. A real click, so onClick gets a MouseEvent as for the mouse.
            event.preventDefault()
            event.currentTarget.click()
        }
    }

    return (
        <i
            role={clickable ? 'button' : named ? 'img' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-hidden={!clickable && !named ? true : undefined}
            {...rest}
            style={{
                fontSize: iconSizes[size] || iconSizes.default,
                color: color || iconColors.default,
                cursor: clickable ? 'pointer' : 'default',
                ...style
            }}
            onClick={onClick}
            onKeyDown={clickable || onKeyDown ? keyDown : undefined}
            className={[icon, clickable && focusableClass, className].filter(Boolean).join(' ')}
        />
    )
}
